import { Users, Crown, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SegmentCounts } from "@/hooks/use-email";

interface SegmentSelectorProps {
  value: string;
  onChange: (value: string) => void;
  showLabel?: boolean;
  counts?: SegmentCounts | null;
}

const segments = [
  {
    value: "all",
    label: "All Subscribers",
    description: "Send to everyone",
    icon: Users,
    color: "text-blue-500",
  },
  {
    value: "pro",
    label: "Pro Only",
    description: "Premium subscribers",
    icon: Crown,
    color: "text-amber-500",
  },
  {
    value: "free",
    label: "Free Only",
    description: "Free tier users",
    icon: Sparkles,
    color: "text-emerald-500",
  },
];

export function SegmentSelector({
  value,
  onChange,
  showLabel = true,
  counts,
}: SegmentSelectorProps) {
  const selectedSegment = segments.find((s) => s.value === value);

  const getCount = (segmentValue: string) => {
    if (!counts) return null;
    return counts[segmentValue as keyof SegmentCounts];
  };

  const formatCount = (count: number | null | undefined) => {
    if (count === null || count === undefined) return "";
    return count.toLocaleString();
  };

  return (
    <div className="space-y-2">
      {showLabel && (
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Audience
        </Label>
      )}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12">
          <SelectValue>
            {selectedSegment && (
              <div className="flex items-center gap-2">
                <selectedSegment.icon
                  className={`w-4 h-4 ${selectedSegment.color}`}
                />
                <span>{selectedSegment.label}</span>
                {counts && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({formatCount(getCount(selectedSegment.value))})
                  </span>
                )}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {segments.map((segment) => {
            const count = getCount(segment.value);
            return (
              <SelectItem
                key={segment.value}
                value={segment.value}
                className="py-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-1.5 rounded-lg bg-muted ${segment.color}`}
                  >
                    <segment.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{segment.label}</p>
                      {count !== null && count !== undefined && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {formatCount(count)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {segment.description}
                    </p>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
